import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import {
  fetchMyVehicleReservations,
  getReservationByUsers,
} from "@/apis/reservation";
import Layout from "@/components/Layout";
import { RESERVATION_STATE } from "@/constants/common";
import { HOME_MAIN_URL, MYPAGE_MAIN_URL } from "@/constants/routes";
import useModal from "@/hooks/useModal";
import DashboardCard from "@/pages/Dashboard/components/DashboardCard";
import UserReservationDetailModal from "@/pages/Dashboard/components/UserReservationDetailModal";
import UserReservationSection from "@/pages/Dashboard/components/UserReservationSection";
import {
  VEHICLE_STATUS_KO,
  formatTime,
  statusStyle,
  vehicleStatusStyle,
} from "@/pages/Dashboard/shared";
import type { UnifiedRow } from "@/pages/Dashboard/shared";
import useLoginStore from "@/store/loginStore";
import useUserStore from "@/store/userStore";
import type { ReservationByState } from "@/types/reservation";
import type { VehicleReservation } from "@/types/vehicleReservation";

const UserDashboard = () => {
  const navigate = useNavigate();
  const user = useUserStore();
  const { tokenId } = useLoginStore();
  const [myReservations, setMyReservations] = useState<ReservationByState[]>(
    [],
  );
  const [myVehicles, setMyVehicles] = useState<VehicleReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState<UnifiedRow | null>(null);
  const detailModal = useModal();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [placeRes, vehicleRes] = await Promise.all([
        getReservationByUsers(0, 100, tokenId),
        fetchMyVehicleReservations(tokenId),
      ]);
      setMyReservations(placeRes?.content || []);
      setMyVehicles(vehicleRes || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 장소 예약 통계
  const placePending = myReservations.filter(r => r.sttCd === "request");
  const placeApproved = myReservations.filter(r => r.sttCd === "approve");
  const placeRejected = myReservations.filter(
    r => r.sttCd === "reject" || r.sttCd === "cancel",
  );
  const placeUpcoming = placeApproved.filter(r => {
    const dt = `${r.rsvtDt.slice(0, 4)}-${r.rsvtDt.slice(4, 6)}-${r.rsvtDt.slice(6, 8)}`;

    return !dayjs(dt).isBefore(dayjs(), "day");
  });
  const placeRecent = placeApproved.filter(r => {
    const dt = `${r.rsvtDt.slice(0, 4)}-${r.rsvtDt.slice(4, 6)}-${r.rsvtDt.slice(6, 8)}`;

    return dayjs(dt).isBefore(dayjs(), "day");
  });

  // 차량 예약 통계
  const vehiclePending = myVehicles.filter(v => v.status === "pending");
  const vehicleApproved = myVehicles.filter(v => v.status === "approved");
  const vehicleRejected = myVehicles.filter(
    v => v.status === "rejected" || v.status === "cancelled",
  );
  const vehicleUpcoming = vehicleApproved.filter(
    v => !dayjs(v.reservationDate).isBefore(dayjs(), "day"),
  );

  // 통합 리스트 (장소 + 차량 합침)
  const toPlaceRow = (
    r: ReservationByState,
    sLabel: string,
    sClass: string,
  ): UnifiedRow => ({
    key: `p-${r.rsvtId}`,
    type: "place",
    name: r.plcNm,
    time: `${formatTime(r.startTime)}~${formatTime(r.endTime)}`,
    date: `${r.rsvtDt.slice(2, 4)}.${r.rsvtDt.slice(4, 6)}.${r.rsvtDt.slice(6, 8)}`,
    detail: r.useCnts,
    statusLabel: sLabel,
    statusClass: sClass,
    fullDate:
      r.rsvtDt.length >= 8
        ? dayjs(
            `${r.rsvtDt.slice(0, 4)}-${r.rsvtDt.slice(4, 6)}-${r.rsvtDt.slice(6, 8)}`,
          ).format("YYYY년 M월 D일 (dd)")
        : "-",
    userName: r.userName || "-",
    telNo: r.telNo || "-",
  });

  const toVehicleRow = (
    v: VehicleReservation,
    sLabel: string,
    sClass: string,
  ): UnifiedRow => ({
    key: `v-${v.id}`,
    type: "vehicle",
    name: v.destination,
    time: v.drivingTime,
    date: dayjs(v.reservationDate).format("YY.MM.DD"),
    detail: v.purpose,
    statusLabel: sLabel,
    statusClass: sClass,
    fullDate: v.reservationDate
      ? dayjs(v.reservationDate).format("YYYY년 M월 D일 (dd)")
      : "-",
    userName: v.applicantName || "-",
    telNo: v.applicantPhone || "-",
  });

  const allPending: UnifiedRow[] = [
    ...placePending.map(r => toPlaceRow(r, "대기", statusStyle.request)),
    ...vehiclePending.map(v =>
      toVehicleRow(v, "대기", vehicleStatusStyle.pending),
    ),
  ];

  const allUpcoming: UnifiedRow[] = [
    ...placeUpcoming.map(r => toPlaceRow(r, "승인", statusStyle.approve)),
    ...vehicleUpcoming.map(v =>
      toVehicleRow(v, "승인", vehicleStatusStyle.approved),
    ),
  ];

  const allRejected: UnifiedRow[] = [
    ...placeRejected.map(r =>
      toPlaceRow(
        r,
        RESERVATION_STATE[r.sttCd] || r.sttCd,
        statusStyle[r.sttCd] || statusStyle.cancel,
      ),
    ),
    ...vehicleRejected.map(v =>
      toVehicleRow(
        v,
        VEHICLE_STATUS_KO[v.status] || v.status,
        vehicleStatusStyle[v.status] || vehicleStatusStyle.cancelled,
      ),
    ),
  ];

  const handleRowClick = (row: UnifiedRow) => {
    setSelectedRow(row);
    detailModal.onOpen();
  };

  const closeDetailModal = () => {
    detailModal.onClose();
    setSelectedRow(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center text-gray-400">
          로딩 중...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {user.userName}님, 안녕하세요
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {dayjs().format("YYYY년 M월 D일 (dd)")}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <DashboardCard
            title="장소 예약 승인 대기"
            count={placePending.length}
            label="건의 장소 예약 요청"
            color="amber"
          />
          <DashboardCard
            title="차량 예약 승인 대기"
            count={vehiclePending.length}
            label="건의 차량 예약 요청"
            color="blue"
          />
          <DashboardCard
            title="최근 승인된 예약"
            count={
              placeRecent.length +
              vehicleApproved.filter(v =>
                dayjs(v.reservationDate).isBefore(dayjs(), "day"),
              ).length
            }
            label="건의 예약이 완료됨"
            color="green"
          />
          <DashboardCard
            title="다가오는 예약"
            count={placeUpcoming.length + vehicleUpcoming.length}
            label="건의 예약이 예정됨"
            color="red"
            onClick={() => navigate(HOME_MAIN_URL)}
          />
        </div>

        {/* Detail Sections */}
        <div className="grid gap-4 lg:grid-cols-3">
          <UserReservationSection
            title="승인 대기"
            emptyText="대기 중인 예약이 없습니다"
            rows={allPending}
            onRowClick={handleRowClick}
            headerAction={
              allPending.length > 0 && (
                <button
                  type="button"
                  onClick={() => navigate(`${MYPAGE_MAIN_URL}?tab=1`)}
                  className="text-xs text-primary hover:underline"
                >
                  전체 보기
                </button>
              )
            }
          />
          <UserReservationSection
            title="다가오는 예약"
            emptyText="예정된 예약이 없습니다"
            rows={allUpcoming}
            onRowClick={handleRowClick}
            headerAction={
              <button
                type="button"
                onClick={() => navigate(HOME_MAIN_URL)}
                className="text-xs text-primary hover:underline"
              >
                캘린더 보기
              </button>
            }
          />
          <UserReservationSection
            title="반려 / 취소"
            emptyText="반려 또는 취소된 예약이 없습니다"
            rows={allRejected}
            onRowClick={handleRowClick}
            headerAction={
              allRejected.length > 0 && (
                <button
                  type="button"
                  onClick={() => navigate(`${MYPAGE_MAIN_URL}?tab=1`)}
                  className="text-xs text-primary hover:underline"
                >
                  전체 보기
                </button>
              )
            }
          />
        </div>
      </div>

      {/* 상세 팝업 */}
      {detailModal.isOpen && selectedRow && (
        <UserReservationDetailModal
          row={selectedRow}
          onClose={closeDetailModal}
        />
      )}
    </Layout>
  );
};

export default UserDashboard;
